import { VariantSelectedEvent, VariantUpdateEvent } from '@hvduc/events';
import { yieldToMainThread, getViewParameterValue } from '@hvduc/utilities';

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._pendingRequestUrl = null;
    this._fieldsets = null;
    this._checkedIndices = [];
    this._radios = [];
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  connectedCallback() {
    this._sectionId = this.dataset.sectionId;
    this._showSelectedValue = this.dataset.showSelectedValue === 'true';
    this._fieldsets = this.querySelectorAll('fieldset');
    const fieldsets = this._fieldsets || [];

    fieldsets.forEach((fieldset, index) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"]') ?? []);
      this._radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this._checkedIndices.push([initialCheckedIndex]);
      }

      const optionButtons = fieldset.querySelectorAll('.variant-option__button');
      optionButtons.forEach((button) => {
        button.addEventListener('mouseenter', (e) => this.handleOptionHover(e, fieldset));
        button.addEventListener('mouseleave', (e) => this.handleOptionUnhover(e, fieldset));
      });
    });
  }

  disconnectedCallback() {}

  handleOptionHover(event, fieldset) {
    const legend = fieldset.querySelector('.variant-option__label');
    if (!legend || !this._showSelectedValue) return;

    const optionInput = event.target.querySelector('input[type="radio"]');
    if (!optionInput) return;

    this.handleOptionUnhover(null, fieldset);

    legend.classList.add('hover');
    const span = document.createElement('span');
    span.className = 'variant-option__hover-value';
    span.textContent = optionInput.dataset.optionLabel || '';
    legend.appendChild(span);
  }

  handleOptionUnhover(event, fieldset) {
    const legend = fieldset.querySelector('.variant-option__label');
    if (!legend || !this._showSelectedValue) return;
    legend.classList.remove('hover');
    const span = legend.querySelector('.variant-option__hover-value');
    if (span) span.remove();
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
      }),
    );

    const inBlock = Boolean(
      this.closest('product-card') || this.closest('quick-add-dialog') || this.closest('featured-product-information'),
    );
    const inFeaturedSection = Boolean(this.closest('featured-product-information'));
    const isPDP = this.dataset.templateProductMatch === 'true' && !inBlock && !inFeaturedSection;

    const baseProductUrl = this.dataset.productUrl?.split('?')[0];
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;

    const isConnectedProduct = isPDP && !!connectedProductUrl && connectedProductUrl !== baseProductUrl;

    let context = VARIANT_CHANGE_CONTEXT.PDP;
    if (inBlock) {
      context = VARIANT_CHANGE_CONTEXT.BLOCK;
    } else if (inFeaturedSection) {
      context = VARIANT_CHANGE_CONTEXT.FEATURED;
    } else if (isConnectedProduct) {
      context = VARIANT_CHANGE_CONTEXT.ASSOCIATED;
    }

    const requestUrl = this.buildRequestUrl(selectedOption, null, [], context !== VARIANT_CHANGE_CONTEXT.ASSOCIATED);
    this.fetchUpdatedSection(requestUrl, context);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isPDP) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (isConnectedProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');

    if (target instanceof HTMLInputElement) {
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = this._fieldsets || [];
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this._checkedIndices[fieldsetIndex];
        const radios = this._radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateOptionDisplay(fieldsetIndex, target.dataset.optionLabel);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');

      console.log('TARGET: ', fieldsetIndex);

      if (!Number.isNaN(fieldsetIndex)) {
        this.updateOptionDisplay(fieldsetIndex, newSelectedOption.dataset.optionLabel);
      }
    }
  }

  updateOptionDisplay(fieldsetIndex, selectedOptionLabel) {
    const fieldset = this._fieldsets[fieldsetIndex];
    const selectedOptionLabelEl = fieldset.querySelector('.variant-option__selected-value');
    if (selectedOptionLabelEl) {
      selectedOptionLabelEl.textContent = selectedOptionLabel || '';
    }
  }

  async fetchUpdatedSection(requestUrl, context) {
    console.log('Context:', context); // Log the request URL for debugging
    this._abortController?.abort(); // eslint-disable-line no-unused-expressions
    this._abortController = new AbortController();

    await fetch(requestUrl, { signal: this._abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this._pendingRequestUrl = null;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        console.log('Fetched HTML:', html); // Log the fetched HTML for debugging

        const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
        if (!textContent) return;

        let newProduct;

        switch (context) {
          case VARIANT_CHANGE_CONTEXT.PDP:
            this.updatePdpElement(html);
            break;
          case VARIANT_CHANGE_CONTEXT.BLOCK:
            // newProduct = this.updateVariantPicker(html);
            break;
          case VARIANT_CHANGE_CONTEXT.FEATURED:
            // newProduct = this.updateElement(html);
            break;
          case VARIANT_CHANGE_CONTEXT.ASSOCIATED:
            this.updateMain(html);
            break;
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            }),
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], includeSectionId = true) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this._pendingRequestUrl || this.dataset.productUrl;
    this._pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (includeSectionId && this._sectionId) {
      params.push(`section_id=${this._sectionId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  updatePdpElement(newHtml) {
    const currentMain = this.closest('main');
    if (!currentMain) {
      throw new Error('No current main element found');
    }

    const replaceElement = (selector, errorMessage) => {
      const nextElement = newHtml.querySelector(selector);
      const currentElement = currentMain.querySelector(selector);

      if (!nextElement || !currentElement) {
        throw new Error(errorMessage);
      }

      currentElement.replaceWith(nextElement);
    };

    replaceElement('product-details', 'No product details element found');
    replaceElement('product-media', 'No product media element found');
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    main.replaceWith(newMain);
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
